import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { defaultOnError } from "util/conditional/onError";
import i18n from "i18n";
import UserNotification from "util/UserNotification";
import { deserializeEventProcedures } from "security-app/components/SecurityEvents/EventProcedures/plugin/EventProcedureLibraryForm/List/constants";
import { deserializeEventProcedureSteps } from "security-app/components/SecurityEvents/EventProcedures/plugin/EventProcedureStepLibraryForm/List/constants";
import {
  fetchEventProcedures,
  fetchEventProcedure,
  fetchEventProcedureSteps,
  createEventProcedure,
  updateEventProcedure,
  createEventProcedureStep,
  deleteEventProcedure,
  deleteEventProcedureStep,
  updateEventProcedureStep,
  fetchDecoratedEventProcedure,
  fetchParametersForView,
  fetchLinkedProcedures,
  executeEventProcedureActionBulk
} from "./api/eventProceduresAPI";
export const eventProceduresEntitiesKeyFn = (searchParams) => ["event-procedures", "list", searchParams];
export function useFetchEventProcedures(deserializer = deserializeEventProcedures) {
  const searchEventProcedures = async (searchParams) => {
    const {
      page,
      pageSize,
      query,
      sort: { attributeId, direction }
    } = searchParams;
    return fetchEventProcedures(page, pageSize, query, attributeId, direction).then(deserializer);
  };
  return { eventProceduresEntitiesKeyFn, searchEventProcedures };
}
export function useGetEventProcedures({
  page,
  perPage,
  query = void 0,
  orderBy = void 0,
  direction = void 0
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-event-procedures", page, perPage, query, orderBy, direction],
    queryFn: () => defaultOnError(
      fetchEventProcedures(page, perPage, query, orderBy, direction),
      i18n.t("notifications.fetch_event_procedures_failed", { ns: "enterprise_security_events" })
    ),
    retry: 2,
    placeholderData: keepPreviousData
  });
  return {
    loadingEventProcedures: isLoading,
    eventProcedures: isLoading ? [] : data == null ? void 0 : data.event_procedures,
    pagination: {
      page: (data == null ? void 0 : data.page) || page,
      perPage: (data == null ? void 0 : data.per_page) || perPage,
      total: (data == null ? void 0 : data.total) || 0,
      grandTotal: (data == null ? void 0 : data.grand_total) || 0,
      count: (data == null ? void 0 : data.count) || 0
    }
  };
}
export function useGetEventProcedure(eventProcedureId) {
  const { data, isFetching } = useQuery({
    queryKey: ["get-event-procedure", eventProcedureId],
    queryFn: () => defaultOnError(
      fetchEventProcedure(eventProcedureId),
      i18n.t("notifications.fetch_event_procedure_failed", { ns: "enterprise_security_events" })
    ),
    enabled: !!eventProcedureId,
    retry: 2
  });
  return {
    loadingEventProcedure: isFetching,
    eventProcedure: isFetching ? {} : data
  };
}
export function useGetDecoratedEventProcedure(eventProcedureId, eventId) {
  const { data, isFetching } = useQuery({
    queryKey: ["get-decorated-event-procedure", eventProcedureId, eventId],
    queryFn: () => defaultOnError(
      eventId ? fetchDecoratedEventProcedure(eventProcedureId, eventId) : fetchEventProcedure(eventProcedureId),
      i18n.t("notifications.fetch_event_procedure_failed", { ns: "enterprise_security_events" })
    ),
    enabled: !!eventProcedureId,
    retry: 2
  });
  return isFetching ? {
    loadingEventProcedure: true
  } : {
    loadingEventProcedure: false,
    eventProcedure: data
  };
}
export function useCreateEventProcedure() {
  const queryClient = useQueryClient();
  const {
    mutateAsync,
    data,
    isPending: isLoading
  } = useMutation({
    mutationFn: createEventProcedure,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.event_procedure_created", { ns: "enterprise_security_events" }));
      queryClient.invalidateQueries({ queryKey: ["event-procedures", "list"] });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    createEventProcedure: mutateAsync,
    eventProcedure: isLoading ? {} : data,
    creatingEventProcedure: isLoading
  };
}
export function useUpdateEventProcedure() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateEventProcedure,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.event_procedure_updated", { ns: "enterprise_security_events" }));
      queryClient.invalidateQueries({ queryKey: ["event-procedures", "list"] });
      queryClient.invalidateQueries({ queryKey: ["get-event-procedure"] });
      queryClient.invalidateQueries({ queryKey: ["get-decorated-event-procedure"] });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    updateEventProcedure: mutateAsync,
    updatingEventProcedure: isLoading
  };
}
export function useDeleteEventProcedure() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteEventProcedure,
    onSuccess: () => {
      UserNotification.success(i18n.t("notifications.event_procedure_deleted", { ns: "enterprise_security_events" }));
      queryClient.invalidateQueries({ queryKey: ["event-procedures", "list"] });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    deleteEventProcedure: mutateAsync,
    deletingEventProcedure: isLoading
  };
}
export const eventProcedureStepsEntitiesKeyFn = (searchParams) => [
  "event-procedure-steps",
  "list",
  searchParams
];
export function useFetchEventProcedureSteps(deserializer = deserializeEventProcedureSteps) {
  const searchEventProcedureSteps = async (searchParams) => {
    const {
      page,
      pageSize,
      query,
      sort: { attributeId, direction }
    } = searchParams;
    return fetchEventProcedureSteps(page, pageSize, query, attributeId, direction).then(deserializer);
  };
  return { eventProcedureStepsEntitiesKeyFn, searchEventProcedureSteps };
}
export function useGetEventProcedureSteps({
  page,
  perPage,
  query = void 0,
  orderBy = void 0,
  direction = void 0
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-event-procedure-steps", page, perPage, query, orderBy, direction],
    queryFn: () => defaultOnError(
      fetchEventProcedureSteps(page, perPage, query, orderBy, direction),
      i18n.t("notifications.fetch_event_procedure_steps_failed", { ns: "enterprise_security_events" })
    ),
    retry: 2,
    placeholderData: keepPreviousData
  });
  return {
    loadingEventProcedureSteps: isLoading,
    eventProcedureSteps: isLoading ? [] : data == null ? void 0 : data.event_procedure_steps,
    pagination: {
      page: (data == null ? void 0 : data.page) || page,
      perPage: (data == null ? void 0 : data.per_page) || perPage,
      total: (data == null ? void 0 : data.total) || 0,
      grandTotal: (data == null ? void 0 : data.grand_total) || 0,
      count: (data == null ? void 0 : data.count) || 0
    }
  };
}
export function useCreateEventProcedureStep() {
  const queryClient = useQueryClient();
  const {
    mutateAsync,
    data,
    isPending: isLoading
  } = useMutation({
    mutationFn: createEventProcedureStep,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.event_procedure_step_created", { ns: "enterprise_security_events" })
      );
      queryClient.invalidateQueries({
        queryKey: ["event-procedure-steps", "list"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    createEventProcedureStep: mutateAsync,
    eventProcedureStep: isLoading ? {} : data,
    creatingEventProcedureStep: isLoading
  };
}
export function useUpdateEventProcedureStep() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateEventProcedureStep,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.event_procedure_step_updated", { ns: "enterprise_security_events" })
      );
      queryClient.invalidateQueries({ queryKey: ["event-procedure-steps", "list"] });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    updateEventProcedureStep: mutateAsync,
    updatingEventProcedureStep: isLoading
  };
}
export function useGetLinkedProceduresForStep(stepId) {
  const { data, isFetching } = useQuery({
    queryKey: ["get-procedures-using-step", stepId],
    queryFn: () => defaultOnError(
      fetchLinkedProcedures(stepId),
      i18n.t("notifications.fetch_linked_event_procedures_failed", { ns: "enterprise_security_events" })
    ),
    enabled: !!stepId,
    retry: 2
  });
  return {
    loadingLinkedEventProcedures: isFetching,
    linkedEventProcedures: isFetching ? [] : data
  };
}
export function useDeleteEventProcedureStep() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteEventProcedureStep,
    onSuccess: () => {
      UserNotification.success(
        i18n.t("notifications.event_procedure_step_deleted", { ns: "enterprise_security_events" })
      );
      queryClient.invalidateQueries({ queryKey: ["event-procedure-steps", "list"] });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    deleteEventProcedureStep: mutateAsync,
    deletingEventProcedureStep: isLoading
  };
}
export function useGetParametersForView({ viewId, type }) {
  const { data, isFetching } = useQuery({
    queryKey: ["get-parameters-for-view", viewId, type],
    queryFn: () => defaultOnError(
      fetchParametersForView(viewId, type),
      i18n.t("notifications.fetch_parameters_failed", { ns: "enterprise_security_events" })
    ),
    enabled: !!viewId,
    retry: 2
  });
  return {
    fetchingParameters: isFetching,
    parameters: isFetching ? [] : data
  };
}
export function useExecuteEventProcedureActionBulk() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: executeEventProcedureActionBulk,
    onSuccess: (resp) => {
      if (resp.errors && resp.errors.length > 0) {
        UserNotification.warning(resp.errors.join("\n"));
      } else {
        queryClient.invalidateQueries({ queryKey: ["event-procedures", "list"] });
        queryClient.invalidateQueries({ queryKey: ["get-event-procedure"] });
        queryClient.invalidateQueries({ queryKey: ["get-decorated-event-procedure"] });
      }
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    executeEventProcedureActionBulk: mutateAsync,
    executingEventProcedureActionBulk: isLoading
  };
}
